import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fmtNum, shortDate } from "@/lib/format";
import {
  Users, Search, Download, Phone, User, Home, MapPin,
  GraduationCap, AlertCircle, CheckCircle2,
} from "@/components/icons/console-icons";

type Guardian = {
  id: string;
  full_name: string;
  phone: string | null;
  email: string | null;
  national_id: string | null;
  occupation: string | null;
  household_size: number;
  address: string | null;
  ward_id: string | null;
  ward?: { name: string };
  latitude: number | null;
  longitude: number | null;
};

type StudentGuardian = {
  student_id: string;
  guardian_id: string;
  relationship: string;
  is_primary: boolean;
  student?: { full_name: string; grade: string; school?: { name: string } };
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const RELATIONSHIP_CONFIG: Record<string, { label: string; color: string }> = {
  mother: { label: "Mother", color: "#ec4899" },
  father: { label: "Father", color: "#3b82f6" },
  grandmother: { label: "Grandmother", color: "#a855f7" },
  grandfather: { label: "Grandfather", color: "#6366f1" },
  aunt: { label: "Aunt", color: "#f59e0b" },
  uncle: { label: "Uncle", color: "#0891b2" },
  guardian: { label: "Guardian", color: "#667085" },
  parent: { label: "Parent", color: "#0d6b3d" },
};

export function GuardianRegistry() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const { data: guardians = [], isLoading } = useQuery({
    queryKey: ["zw-guardian-registry", page, pageSize],
    queryFn: async () => {
      const { data } = await supabase
        .from("zw_guardians")
        .select(`
          id, full_name, phone, email, national_id, occupation, household_size,
          address, ward_id, latitude, longitude,
          ward:zw_wards(name)
        `)
        .order("full_name")
        .range(page * pageSize, (page + 1) * pageSize - 1);
      return (data ?? []) as unknown as Guardian[];
    },
  });

  const { data: studentGuardians = [] } = useQuery({
    queryKey: ["zw-student-guardians"],
    queryFn: async () => {
      const { data } = await supabase
        .from("zw_student_guardians")
        .select(`
          student_id, guardian_id, relationship, is_primary,
          student:zw_students(full_name, grade, school:schools(name))
        `)
        .limit(500);
      return (data ?? []) as unknown as StudentGuardian[];
    },
  });

  const { data: totalGuardians } = useQuery({
    queryKey: ["zw-guardian-registry-count"],
    queryFn: async () => (await supabase.from("zw_guardians").select("*", { count: "exact", head: true })).count ?? 0,
  });

  // Build a map of guardian_id -> children
  const guardianChildrenMap = useMemo(() => {
    const m: Record<string, StudentGuardian[]> = {};
    studentGuardians.forEach(sg => {
      if (!m[sg.guardian_id]) m[sg.guardian_id] = [];
      m[sg.guardian_id].push(sg);
    });
    return m;
  }, [studentGuardians]);

  const filtered = useMemo(() =>
    guardians.filter(g => {
      if (search && !g.full_name.toLowerCase().includes(search.toLowerCase()) && !(g.phone ?? "").includes(search)) return false;
      return true;
    }),
  [guardians, search]);

  const FS = "'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-muted-foreground">Loading guardian registry...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <div className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0d6b3d 0%, #0a4a30 50%, #062818 100%)" }}>
        <div className="relative mx-auto max-w-7xl px-4 py-7 md:px-6 md:py-9">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em]" style={{ color: "#d4a017" }}>
            <span className="h-px w-6" style={{ background: "#c96a4a" }} /> BRECCS Zimbabwe · Guardian & Household Registry
          </div>
          <h1 className="mt-2 text-2xl font-bold text-white md:text-3xl" style={{ fontFamily: FS }}>
            Guardian & Household Registry
          </h1>
          <p className="mt-1 text-sm text-white/70">
            {fmtNum(totalGuardians ?? 0)} registered guardians with linked children across {fmtNum(guardians.length)} records. Full household relationships visible.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-6">
        {/* Search */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by guardian name or phone..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Download className="mr-1 h-4 w-4" /> Export
          </Button>
        </div>

        {/* Guardian Cards */}
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Users className="h-12 w-12 text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium text-muted-foreground">No guardians found</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Try adjusting your search</p>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map(g => {
              const children = guardianChildrenMap[g.id] ?? [];
              return (
                <Card key={g.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-stone-200 text-xs font-bold text-stone-700 dark:bg-stone-800 dark:text-stone-300">
                      {getInitials(g.full_name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{g.full_name}</div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
                        {g.phone && <span className="flex items-center gap-0.5"><Phone className="h-3 w-3" />{g.phone}</span>}
                        {g.occupation && <span>· {g.occupation}</span>}
                      </div>
                      {g.address && (
                        <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground">
                          <Home className="h-3 w-3" />{g.address}
                        </div>
                      )}
                      {g.ward?.name && (
                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                          <MapPin className="h-3 w-3" />Ward: {g.ward.name}
                        </div>
                      )}
                      <div className="mt-1 flex items-center gap-2 text-[10px]">
                        <Badge variant="outline" className="text-[9px]">Household: {g.household_size}</Badge>
                        {g.national_id && <Badge variant="outline" className="text-[9px]">ID: {g.national_id}</Badge>}
                      </div>
                    </div>
                  </div>

                  {/* Linked Children */}
                  {children.length > 0 && (
                    <div className="mt-3 border-t pt-2.5">
                      <div className="text-[9px] uppercase tracking-wider text-muted-foreground mb-2">
                        Linked Children ({children.length})
                      </div>
                      <div className="space-y-1.5">
                        {children.slice(0, 5).map(c => {
                          const rc = RELATIONSHIP_CONFIG[c.relationship] ?? { label: c.relationship, color: "#667085" };
                          return (
                            <div key={c.student_id} className="flex items-center gap-2 rounded-md bg-stone-50 dark:bg-stone-900 px-2.5 py-1.5">
                              <div className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[9px] font-bold text-white" style={{ background: rc.color }}>
                                {c.is_primary ? "P" : "S"}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-medium truncate">{c.student?.full_name ?? "—"}</div>
                                <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                                  <GraduationCap className="h-2.5 w-2.5" />{c.student?.grade ?? "—"}
                                  {c.student?.school?.name && <span>· {c.student.school.name}</span>}
                                </div>
                              </div>
                              <Badge variant="outline" className="text-[9px] shrink-0" style={{ borderColor: `${rc.color}30`, color: rc.color }}>
                                {rc.label}
                              </Badge>
                            </div>
                          );
                        })}
                        {children.length > 5 && (
                          <div className="text-[10px] text-muted-foreground pl-1">+ {children.length - 5} more children</div>
                        )}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalGuardians && totalGuardians > pageSize && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page + 1} of {Math.ceil((totalGuardians ?? 0) / pageSize)}
            </span>
            <Button variant="outline" size="sm" disabled={(page + 1) * pageSize >= (totalGuardians ?? 0)} onClick={() => setPage(p => p + 1)}>
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
